import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';

type Args = {
  active: boolean;
  onDetected: (text: string) => void;
};

type NativeBarcodeDetector = new (opts: { formats: string[] }) => {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
};

export function useBarcodeScanner({ active, onDetected }: Args) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let stream: MediaStream | null = null;
    let zxingController: { stop: () => void } | null = null;
    let rafId = 0;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        await video.play();

        const Native = (window as unknown as { BarcodeDetector?: NativeBarcodeDetector })
          .BarcodeDetector;
        if (Native) {
          const det = new Native({
            formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'qr_code'],
          });
          const loop = async () => {
            if (stopped) return;
            try {
              const codes = await det.detect(video);
              if (codes[0]?.rawValue) {
                onDetected(codes[0].rawValue);
                return;
              }
            } catch {}
            rafId = requestAnimationFrame(loop);
          };
          loop();
        } else {
          const reader = new BrowserMultiFormatReader();
          const control = await reader.decodeFromVideoElement(video, (result) => {
            if (result) onDetected(result.getText());
          });
          zxingController = control;
        }
      } catch (e) {
        setErr((e as Error).message);
      }
    })();

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      zxingController?.stop();
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [active, onDetected]);

  return { videoRef, err };
}
